import React from 'react';
import { Award, Heart, Users, Target, BookOpen, Globe, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

const AboutUs = () => {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO 
        title="About Us | Black-Owned, Woman-Led Real Estate Brokerage"
        description="Meet the team behind Bespoke Realty. We're a Black-owned, woman-led real estate brokerage in Memphis, TN & North Mississippi committed to personalized service and building lasting relationships."
        keywords="about Bespoke Realty, Black-owned real estate Memphis, woman-led brokerage, Memphis real estate agents, Tennessee Mississippi realtors"
      />
      <StructuredData schema={[
        schemas.organization(),
        schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }]),
      ]} />
      {/* Hero Banner - Image Only */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/qkz87feq_Theheartofbespoke1.png)',
          }}
        ></div>
      </section>

      {/* Benjamin & Telesa - Leadership Photo & Story */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-12 mb-12">
            <div className="md:w-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/7zbk36uz_BenandTee.jpg"
                alt="Benjamin and Telesa Wright, Owners of Bespoke Realty"
                className="w-full rounded-lg shadow-xl object-cover"
              />
            </div>
            <div className="md:w-1/2">
              <h2 className="text-4xl font-bold mb-6">Partners in Life & Leadership</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Bespoke Realty</strong> is a full-service real estate brokerage proudly owned and operated by <strong>Benjamin and Telesa Wright</strong> — partners in both life and leadership.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Married for <strong>19 years</strong>, they share <strong>six wonderful children</strong> and <strong>three beautiful grandchildren</strong>. Together, they've built more than a brokerage — they've built a family-driven legacy rooted in <strong>faith, excellence, and service</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-gradient-to-br from-[#BCA480]/10 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            At Bespoke Realty, our mission is simple yet powerful: to provide <strong>customizable real estate services</strong> with <strong>integrity, professionalism, and respect</strong> for every client we serve.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            We are more than agents; we are <strong>advocates, educators, and innovators</strong> who believe every home has a story — and every client deserves an experience as unique as they are.
          </p>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Our Commitment to You</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Heart className="h-12 w-12 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Unparalleled Customer Service</h3>
              <p className="text-gray-600">We prioritize people over profits, ensuring every client receives exceptional care and attention.</p>
            </div>
            <div className="text-center p-6">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Target className="h-12 w-12 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Unmatched Market Knowledge</h3>
              <p className="text-gray-600">We empower smart, confident decisions with deep local expertise and strategic insights.</p>
            </div>
            <div className="text-center p-6">
              <div className="inline-block p-4 bg-[#BCA480]/10 rounded-full mb-4">
                <Award className="h-12 w-12 text-[#BCA480]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Cutting-Edge Marketing</h3>
              <p className="text-gray-600">Innovative strategies that help clients reach their real estate goals with precision and grace.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cali Meets Country */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Cali Meets Country</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4 text-[#BCA480]">Benjamin Wright</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A <strong>California native</strong>, Benjamin embodies confidence, structure, and steady leadership. His vision and strategic mindset guide Bespoke Realty to new heights.
              </p>
              <p className="text-gray-600 italic">
                "Excellence is not an act, but a way of life."
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-bold mb-4 text-[#BCA480]">Telesa Wright</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Born and raised in <strong>Mississippi</strong>, Telesa brings creativity, compassion, and connection to every relationship she builds. Her warmth and attention to detail create a welcoming environment for all.
              </p>
              <p className="text-gray-600 italic">
                "People before profits, always."
              </p>
            </div>
          </div>
          <p className="text-center text-lg text-gray-700 mt-8 leading-relaxed">
            Together, they embody <em>"Cali meets Country"</em> — a partnership fueled by faith, purpose, and a shared belief that <strong>excellence is not an act, but a way of life</strong>.
          </p>
        </div>
      </section>

      {/* Industry Leadership */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Leading Beyond Bespoke</h2>
          <p className="text-lg text-gray-700 text-center leading-relaxed mb-10">
            Their influence extends far beyond Bespoke Realty, shaping the future of diversity, inclusion, and innovation in real estate.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#BCA480]/10 to-white p-8 rounded-lg border-l-4 border-[#BCA480]">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Users className="h-10 w-10 text-[#BCA480]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Benjamin Wright</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Serves on the <strong>Board of the National Association of Real Estate Brokers (NAREB)</strong>, helping shape the future of diversity and inclusion in real estate.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#BCA480]/10 to-white p-8 rounded-lg border-l-4 border-[#BCA480]">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Home className="h-10 w-10 text-[#BCA480]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Telesa Wright</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Serves on the <strong>Board of the National Association of Minority Mortgage Bankers of America (NAMMBA)</strong>, championing innovation, leadership, and empowerment within the industry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Touch */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Beyond the Office</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When they're not leading their agents or serving their community, you'll find Benjamin and Telesa <strong>reading, traveling together, playing golf, or working on DIY projects</strong> — often with a good laugh and a shared vision for what's next.
          </p>
          <div className="bg-[#BCA480]/10 p-8 rounded-lg">
            <p className="text-xl text-gray-700 leading-relaxed italic">
              "In our company, we don't just value profits — we value <strong>people</strong>. We believe in passion over revenue, integrity over popularity, and innovation over complacency. Our values aren't just words; they're our way of life."
            </p>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Who We Are</h2>
          <p className="text-center text-xl text-gray-700 mb-12">
            <strong>Creative. Innovative. Honest. Family-Oriented. Smart-Working.</strong>
          </p>
          <div className="bg-gradient-to-br from-[#434345] to-[#BCA480] text-white p-12 rounded-lg text-center">
            <h3 className="text-3xl font-bold mb-6">Our Mantra</h3>
            <p className="text-2xl leading-relaxed italic">
              Excellence is not a destination — it's a continuous journey that never ends.<br />
              <span className="text-[#BCA480] font-bold">Empower it, and it will empower you.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Meet Our AI Team - Auri & Nova */}
      <section id="auri" className="py-20 bg-gradient-to-br from-[#BCA480]/10 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Meet Our AI Team</h2>
            <p className="text-xl text-gray-600">Innovation, Coordination, and Concierge Service — Powered by Intelligence</p>
          </div>

          {/* Auri Section */}
          <div className="flex flex-col md:flex-row items-center gap-12 mb-16 bg-white rounded-lg p-8 shadow-lg" data-testid="auri-section">
            <div className="md:w-1/3 relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_5290d281-24fe-4df4-8f7b-f48fa4c792c6/artifacts/fw2biquj_ChatGPT%20Image%20May%2023%2C%202026%2C%2002_09_38%20PM.png"
                alt="Auri, Bespoke Realty's AI Transaction Coordinator"
                className="w-full max-w-sm mx-auto rounded-lg shadow-lg"
              />
              <div className="absolute top-4 right-4 bg-[#BCA480] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                AI POWERED
              </div>
            </div>
            <div className="md:w-2/3">
              <h3 className="text-3xl font-bold mb-4">Auri — AI Transaction Coordinator</h3>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Auri is Bespoke Realty's AI-powered Transaction Coordinator. From contract to close, Auri tracks key dates, sends reminders, and keeps every party on schedule so our agents can focus on you.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Expect seamless updates, clear communication, and a calm, concierge-level experience — every time.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  asChild 
                  className="bg-[#BCA480] hover:bg-[#a89470]"
                >
                  <Link to="/buy#buyer-process">How Auri Helps Buyers</Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline"
                  className="border-[#BCA480] text-[#BCA480] hover:bg-[#BCA480] hover:text-white"
                >
                  <Link to="/agent-portal">For Agents: Open Auri Dashboard</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Nova Section */}
          <div className="flex flex-col md:flex-row-reverse items-center gap-12 mb-16 bg-white rounded-lg p-8 shadow-lg" data-testid="nova-section">
            <div className="md:w-1/3 relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_5290d281-24fe-4df4-8f7b-f48fa4c792c6/artifacts/4wrpbx58_ChatGPT%20Image%20May%2023%2C%202026%2C%2002_09_50%20PM.png"
                alt="Nova, Bespoke Realty's AI Concierge"
                className="w-full max-w-sm mx-auto rounded-lg shadow-lg"
              />
              <div className="absolute top-4 right-4 bg-[#BCA480] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                AI POWERED
              </div>
            </div>
            <div className="md:w-2/3">
              <h3 className="text-3xl font-bold mb-4">Nova — AI Concierge</h3>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Nova is the warm, boutique voice of Bespoke Realty online. She welcomes every visitor, answers your questions about buying, selling, and the home journey, and connects you with the right agent when you're ready.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Confident, attentive, and never generic — Nova reflects the same crafted experience our agents bring to every client.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  asChild 
                  className="bg-[#BCA480] hover:bg-[#a89470]"
                >
                  <Link to="/">Chat with Nova</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center mt-12">
            <p className="text-2xl text-gray-700 font-bold">
              Building the Framework for Bespoke Excellence
            </p>
          </div>
        </div>
      </section>

      {/* Beau's Story Section */}
      <section id="beau" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Beau — The Spirit of Bespoke</h2>
          <div className="flex flex-col md:flex-row-reverse items-center gap-12">
            <div className="md:w-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/0469dy7q_Beau.png"
                alt="Beau, Bespoke Realty's hummingbird mascot with compass pendant"
                className="w-full max-w-md mx-auto"
              />
            </div>
            <div className="md:w-1/2">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Beau, our Bespoke hummingbird, symbolizes joyful focus and forward motion — small but mighty, precise, and always purposeful.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Beau wears a compass pendant to represent guidance and direction, and the subtle "B" pattern in his wings reminds us of the Bespoke touch in everything we do.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Look for Beau guiding you through helpful tips, milestones, and celebrations across our site and at Bespoke Realty events.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Diversity */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Committed to Our Community</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Bespoke Realty isn't just a business—it's part of the fabric of Mississippi, Tennessee, and Arkansas. 
            We're committed to supporting local initiatives, promoting diversity in real estate, and 
            helping families build wealth through property ownership.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            As a Black-owned, woman-led brokerage, we take pride in representing and uplifting our communities. 
            Whether you're a client looking to buy your first home, an investor building your portfolio, 
            or an agent seeking a supportive brokerage — <strong>you'll find a home at Bespoke Realty</strong>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#BCA480] to-[#a89470] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Experience the Bespoke Difference?</h2>
          <p className="text-xl mb-8">Served the WRIGHT Way — Let's start your real estate journey together.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-[#434345] hover:bg-gray-100 px-8"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 px-8"
            >
              <Link to="/apply">Join Our Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
